export interface ViewModel {
  status: Status
  messages: Messages
  nextPage: NextPage
  alertSection?: AlertSection
}
interface Status {
  label: string
  icon: 'success' | 'cancel' | 'error'
}
interface Messages {
  info: string[]
  error: string[]
}
interface NextPage {
  path: string
  buttonLabel: string
  autoRedirect: boolean
  requireLogin: boolean
  buttonMessage?: string
}
interface AlertSection {
  title: string
  messages: string[]
}

const paymentSuccessfullyDoneStatus: Status = {
  label: '決済が完了しました',
  icon: 'success',
}
const paymentErrorStatus: Status = {
  label: '決済エラー',
  icon: 'error',
}
const paymentCanceledStatus: Status = {
  label: '決済が中断されました',
  icon: 'cancel',
}
/**
 * パラメータ不正や予期せぬエラーなどの場合に利用される
 */
const unexpectedErrorStatus: Status = {
  label: '決済が失敗した可能性があります',
  icon: 'error',
}

const orderCompleteNextPage: Omit<NextPage, 'path'> = {
  buttonLabel: '注文内容を確認する',
  autoRedirect: true,
  requireLogin: false,
}
const orderHistoryNextPage: NextPage = {
  path: '/s/mypage/order',
  buttonLabel: '注文内容を確認する',
  autoRedirect: false,
  requireLogin: false,
}
const orderHistoryViaLoginNextPage: NextPage = {
  path: '/s/login?next=/s/mypage/order',
  buttonLabel: 'ログインする',
  autoRedirect: false,
  requireLogin: true,
  buttonMessage: 'ご注文内容の確認にはログインが必要です',
}
const reorderNextPage: NextPage = {
  path: '/s/mypage/order',
  buttonLabel: '決済を再開する',
  autoRedirect: false,
  requireLogin: false,
}
const reorderViaLoginNextPage: NextPage = {
  path: '/s/login?next=/s/mypage/order',
  buttonLabel: 'ログインする',
  autoRedirect: false,
  requireLogin: true,
  buttonMessage: '決済を再開するにはログインが必要です',
}

const checkOrderHistoryAlertSection: AlertSection = {
  title: '必ずご確認ください',
  messages: [
    'マイページ＞ご購入履歴をご確認ください。',
    '「注文完了（未発送）」や「出荷準備中」と表示されている場合は、正常に決済が完了しております。「決済未完了」となっている場合は決済が正常に完了していない可能性があります。',
    'しばらくお時間をおいても「決済未完了」からステータスが更新されない場合、各種決済も順次自動キャンセルされますのでご安心ください。',
  ],
}
const reorderAlertSection: AlertSection = {
  title: '決済を再開するには',
  messages: [
    'マイページ＞ご購入履歴からご注文の決済を再開していただけます。',
    '1時間以上決済が確認できない場合は、ご注文を自動でキャンセルさせていただきます。',
  ],
}
/**
 * 外部決済からリダイレクトされないことがあるため、
 * 暫定的にキャンセル画面に注文完了の可能性があるメッセージを追加している
 */
const reorderOnCancelAlertSection: AlertSection = {
  title: '決済を再開するには',
  messages: [
    'マイページ＞ご購入履歴からご注文の決済を再開していただけます。',
    '1時間以上決済が確認できない場合は、ご注文を自動でキャンセルさせていただきます。',
    '「注文完了（未発送）」や「出荷準備中」と表示されている場合は、正常に決済が完了しております。',
  ],
}

export class ViewModelBuilder {
  static forPaymentSuccessfullyDone(): ViewModelBuilder {
    return ViewModelBuilder.createWithStatus(paymentSuccessfullyDoneStatus)
  }
  static forPaymentError(): ViewModelBuilder {
    return ViewModelBuilder.createWithStatus(paymentErrorStatus)
  }
  static forPaymentCanceled(): ViewModelBuilder {
    return ViewModelBuilder.createWithStatus(paymentCanceledStatus)
  }
  static forUnexpectedError(): ViewModelBuilder {
    return ViewModelBuilder.createWithStatus(unexpectedErrorStatus)
  }
  private static createWithStatus(status: Status): ViewModelBuilder {
    return new ViewModelBuilder(status, [], [], undefined)
  }

  private constructor(
    private readonly status: Status,
    private readonly infoMessages: string[],
    private readonly errorMessages: string[],
    private readonly alertSection?: AlertSection
  ) {}

  addInfoMessage(message: string): ViewModelBuilder {
    return new ViewModelBuilder(
      this.status,
      [...this.infoMessages, message],
      this.errorMessages,
      this.alertSection
    )
  }

  addErrorMessage(message: string): ViewModelBuilder {
    return new ViewModelBuilder(
      this.status,
      this.infoMessages,
      [...this.errorMessages, message],
      this.alertSection
    )
  }

  addGuideToOrderCompleteInfoMessage(): ViewModelBuilder {
    return this.addInfoMessage(
      'ご注文内容をご確認いただけるページを表示します。'
    )
  }
  addCheckOrderHistoryErrorMessage(): ViewModelBuilder {
    return this.addErrorMessage(
      'ご注文内容は購入履歴をご確認ください。'
    )
  }
  addMaybeErrorErrorMessage(): ViewModelBuilder {
    return this.addErrorMessage(
      '注文が正常に完了しなかった可能性があります。ご注文履歴をご確認ください。'
    )
  }
  addGuideToOrderHistoryErrorMessage(): ViewModelBuilder {
    return this.addErrorMessage(
      'お手数ですが、念のためご購入履歴をご確認ください。'
    )
  }
  addGuideToReorderErrorMessage(): ViewModelBuilder {
    return this.addErrorMessage(
      '決済処理でエラーが発生しました。お手数ですが注文履歴をご確認の上、決済手続きを再開してください。'
    )
  }

  withCheckOrderHistoryAlertSection(): ViewModelBuilder {
    return new ViewModelBuilder(
      this.status,
      this.infoMessages,
      this.errorMessages,
      checkOrderHistoryAlertSection
    )
  }
  withReorderAlertSection(): ViewModelBuilder {
    return new ViewModelBuilder(
      this.status,
      this.infoMessages,
      this.errorMessages,
      reorderAlertSection
    )
  }
  withReorderAlertSectionOnCancel(): ViewModelBuilder {
    return new ViewModelBuilder(
      this.status,
      this.infoMessages,
      this.errorMessages,
      reorderOnCancelAlertSection
    )
  }

  buildWithOrderCompleteLink(orderCompletePath: string): ViewModel {
    return {
      status: this.status,
      messages: {
        info: this.infoMessages,
        error: this.errorMessages,
      },
      nextPage: {
        ...{ path: orderCompletePath },
        ...orderCompleteNextPage,
      },
      alertSection: this.alertSection,
    }
  }
  buildWithOrderHistoryLink(): ViewModel {
    return {
      status: this.status,
      messages: {
        info: this.infoMessages,
        error: this.errorMessages,
      },
      nextPage: orderHistoryNextPage,
      alertSection: this.alertSection,
    }
  }
  buildWithOrderHistoryViaLoginLink(): ViewModel {
    return {
      status: this.status,
      messages: {
        info: this.infoMessages,
        error: this.errorMessages,
      },
      nextPage: orderHistoryViaLoginNextPage,
      alertSection: this.alertSection,
    }
  }
  buildWithReorderLink(): ViewModel {
    return {
      status: this.status,
      messages: {
        info: this.infoMessages,
        error: this.errorMessages,
      },
      nextPage: reorderNextPage,
      alertSection: this.alertSection,
    }
  }
  buildWithReorderViaLoginLink(): ViewModel {
    return {
      status: this.status,
      messages: {
        info: this.infoMessages,
        error: this.errorMessages,
      },
      nextPage: reorderViaLoginNextPage,
      alertSection: this.alertSection,
    }
  }
}
